import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const SlideContainer = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 1rem;
  padding-top: 4.5rem;
  max-width: 1200px;
  margin: 0 auto;
  overflow-y: auto;

  @media (max-width: 768px) {
    padding: 0.8rem;
    padding-top: 4rem;
  }

  @media (max-width: 480px) {
    padding: 0.5rem;
    padding-top: 3.5rem;
  }
`;

const SlideTitle = styled(motion.h2)`
  font-size: 3.5rem;
  font-weight: 800;
  text-align: center;
  margin-bottom: 2rem;
  background: linear-gradient(45deg, #6F1A3F, #C82B3F);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 1024px) {
    font-size: 3rem;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 768px) {
    font-size: 2.5rem;
    margin-bottom: 1rem;
  }

  @media (max-width: 480px) {
    font-size: 2rem;
    margin-bottom: 1rem;
  }
`;

const ReasonsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  width: 100%;
  margin-bottom: 2rem;

  @media (max-width: 1024px) {
    gap: 1.2rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 480px) {
    gap: 0.8rem;
    margin-bottom: 1rem;
  }
`;

const ReasonCard = styled(motion.div)`
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(111, 26, 63, 0.3);
  border-radius: 15px;
  padding: 1.5rem;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    border-color: rgba(200, 43, 63, 0.6);
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(111, 26, 63, 0.2);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(45deg, #6F1A3F, #C82B3F);
  }

  @media (max-width: 768px) {
    padding: 1.2rem;
  }

  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const ReasonIcon = styled.div`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  background: linear-gradient(45deg, #6F1A3F, #C82B3F);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 0.8rem;
  }

  @media (max-width: 480px) {
    font-size: 1.8rem;
    margin-bottom: 0.5rem;
  }
`;

const ReasonTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 0.8rem;

  @media (max-width: 768px) {
    font-size: 1.2rem;
    margin-bottom: 0.6rem;
  }

  @media (max-width: 480px) {
    font-size: 1.1rem;
    margin-bottom: 0.5rem;
  }
`;



const BottomSection = styled.div`
  margin-top: 2rem;
  text-align: center;
  max-width: 800px;

  @media (max-width: 768px) {
    margin-top: 1.5rem;
  }

  @media (max-width: 480px) {
    margin-top: 1rem;
  }
`;


const PhilosophyText = styled.p`
  font-size: 1rem;
  color: #ccc;
  line-height: 1.5;
  margin-top: 1rem;

  @media (max-width: 768px) {
    font-size: 0.95rem;
    margin-top: 0.8rem;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
    margin-top: 0.6rem;
  }
`;

const Slide10 = () => {
  const reasons = [
    {
      icon: '🎯',
      title: 'Comprehensive Expertise'
    },
    {
      icon: '🚀',
      title: 'Proven Innovation'
    },
    {
      icon: '🤝',
      title: 'Client-Centric Approach'
    },
    {
      icon: '🔄',
      title: 'Full Lifecycle Support'
    },
    {
      icon: '📊',
      title: 'Impact-Driven Results'
    },
    {
      icon: '⚡',
      title: 'Strategic Growth'
    }
  ];

  return (
    <SlideContainer>
      <SlideTitle
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        Why Choose Us
      </SlideTitle>

      <ReasonsGrid>
        {reasons.map((reason, index) => (
          <ReasonCard
            key={index}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: index * 0.1 }}
            whileHover={{ scale: 1.02 }}
          >
            <ReasonIcon>{reason.icon}</ReasonIcon>
            <ReasonTitle>{reason.title}</ReasonTitle>
          </ReasonCard>
        ))}
      </ReasonsGrid>

      <BottomSection>
        <PhilosophyText
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          We believe in collaborative learning, continuous innovation, and involving our clients in every step of the digital transformation journey. Our approach combines technical excellence with strategic thinking to deliver solutions that not only meet current needs but anticipate future challenges.
        </PhilosophyText>
      </BottomSection>
    </SlideContainer>
  );
};

export default Slide10;